import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Popover,
	PopoverTrigger,
	PopoverContent,
	Button,
	Checkbox,
	Input
} from '@heroui/react';
import formatFilterLabel from './formatFilterLabel.js';

/**
 * Multi-select filter with HeroUI `Popover` trigger + custom checklist body.
 *
 * - Trigger shows `label` + collapsed value line (no chips, no layout shift)
 * - Body: auto-focused search input, scrollable list of groups with
 *   indeterminate parent checkbox (select-all-in-group) + indented children
 * - Search clears on popover close; groups with zero visible children are hidden
 *
 * Fully controlled: no Redux, no API calls.
 */
function FilterMultiSelect({
	label,
	entityName,
	placeholder,
	groups = [],
	selected = [],
	onChange,
	enableGroupToggle = true
}) {
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState('');
	const searchRef = useRef(null);

	const placeholderText = placeholder || `All ${entityName}`;

	const selectedSet = useMemo(
		() => new Set((Array.isArray(selected) ? selected : []).map(String)),
		[selected]
	);

	const flatOptions = useMemo(() => {
		const out = [];
		(groups || []).forEach(g =>
			(g.items || []).forEach(item => {
				out.push({ id: String(item.id), label: item.label });
			})
		);
		return out;
	}, [groups]);

	const collapsedLabel = formatFilterLabel(
		Array.from(selectedSet),
		flatOptions,
		entityName,
		placeholderText
	);

	const filteredGroups = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return groups || [];
		return (groups || [])
			.map(g => ({
				...g,
				items: (g.items || []).filter(item =>
					String(item.label).toLowerCase().includes(q)
				)
			}))
			.filter(g => (g.items || []).length > 0);
	}, [groups, search]);

	const emit = useCallback(
		next => {
			if (typeof onChange === 'function') onChange(next);
		},
		[onChange]
	);

	const toggleItem = useCallback(
		itemId => {
			const key = String(itemId);
			const next = new Set(selectedSet);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			emit(Array.from(next));
		},
		[selectedSet, emit]
	);

	const toggleGroup = useCallback(
		visibleItems => {
			const ids = (visibleItems || []).map(i => String(i.id));
			if (ids.length === 0) return;
			const allSelected = ids.every(id => selectedSet.has(id));
			const next = new Set(selectedSet);
			if (allSelected) {
				ids.forEach(id => next.delete(id));
			} else {
				ids.forEach(id => next.add(id));
			}
			emit(Array.from(next));
		},
		[selectedSet, emit]
	);

	const handleOpenChange = useCallback(open => {
		setIsOpen(open);
		if (!open) setSearch('');
	}, []);

	useEffect(() => {
		if (!isOpen) return undefined;
		const t = setTimeout(() => {
			if (searchRef.current && typeof searchRef.current.focus === 'function') {
				searchRef.current.focus();
			}
		}, 0);
		return () => clearTimeout(t);
	}, [isOpen]);

	return (
		<Popover
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			placement="bottom-start"
			offset={6}
			shouldBlockScroll={false}
			classNames={{ content: 'p-0 overflow-hidden' }}
		>
			<PopoverTrigger>
				<Button
					variant="flat"
					disableRipple
					className="h-auto min-w-[180px] max-w-[220px] justify-between gap-2 rounded-medium border border-default-200 bg-default-50 px-3 py-2 hover:bg-default-100"
					aria-label={label}
				>
					<span className="flex min-w-0 flex-col items-start leading-tight">
						<span className="text-[11px] font-medium uppercase tracking-wide text-default-500">
							{label}
						</span>
						<span className="max-w-full truncate text-sm font-medium text-foreground">
							{collapsedLabel}
						</span>
					</span>
					<ChevronDownIcon />
				</Button>
			</PopoverTrigger>

			<PopoverContent>
				<div
					className="flex w-[min(280px,calc(100vw-1rem))] flex-col"
					data-testid={`filter-multi-select-${entityName}`}
				>
					<div className="border-b border-default-200 p-2">
						<Input
							ref={searchRef}
							size="sm"
							placeholder="Search..."
							value={search}
							onValueChange={setSearch}
							variant="flat"
							aria-label={`Search ${entityName}`}
							startContent={<SearchIcon />}
							isClearable
							onClear={() => setSearch('')}
						/>
					</div>

					<div className="max-h-[280px] overflow-y-auto p-2">
						{filteredGroups.length === 0 && (
							<div className="px-2 py-6 text-center text-xs text-default-400">
								{search
									? `No ${entityName} match “${search}”`
									: `No ${entityName} available`}
							</div>
						)}

						{filteredGroups.map(group => {
							const isUngrouped = !group.key || group.key === '__ungrouped__';
							const items = group.items || [];
							const ids = items.map(i => String(i.id));
							const selectedCount = ids.filter(id => selectedSet.has(id)).length;
							const allSelected = ids.length > 0 && selectedCount === ids.length;
							const someSelected = selectedCount > 0 && !allSelected;

							return (
								<div key={group.key || '__u'} className="mb-1 last:mb-0">
									{!isUngrouped && enableGroupToggle && (
										<div className="flex flex-col leading-tight">
											<Checkbox
												size="sm"
												isSelected={allSelected}
												isIndeterminate={someSelected}
												onValueChange={() => toggleGroup(items)}
												classNames={{
													base: 'mx-0 max-w-full w-full rounded-md px-1.5 py-1 hover:bg-default-100 transition-colors duration-150',
													label: 'text-sm font-semibold text-foreground truncate'
												}}
											>
												{group.title}
											</Checkbox>
											{group.subtitle && (
												<span className="pl-[26px] pr-1.5 -mt-0.5 pb-0.5 text-[11px] text-default-500 truncate block">
													{group.subtitle}
												</span>
											)}
										</div>
									)}

									{!isUngrouped && !enableGroupToggle && (
										<div className="px-1.5 py-1 flex flex-col leading-tight">
											<span className="text-xs font-semibold uppercase tracking-wide text-default-500 truncate">
												{group.title}
											</span>
											{group.subtitle && (
												<span className="text-[11px] text-default-400 truncate normal-case">
													{group.subtitle}
												</span>
											)}
										</div>
									)}

									<div className={isUngrouped ? '' : 'pl-5'}>
										{items.map(item => {
											const itemId = String(item.id);
											return (
												<Checkbox
													key={itemId}
													size="sm"
													isSelected={selectedSet.has(itemId)}
													onValueChange={() => toggleItem(itemId)}
													classNames={{
														base: 'mx-0 max-w-full w-full rounded-md px-1.5 py-1 hover:bg-default-100 transition-colors duration-150',
														label: 'text-sm text-foreground'
													}}
												>
													{item.label}
												</Checkbox>
											);
										})}
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}

function ChevronDownIcon() {
	return (
		<svg
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			className="shrink-0 text-default-500"
			aria-hidden="true"
		>
			<path
				d="M6 9l6 6 6-6"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function SearchIcon() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 24 24"
			fill="none"
			className="shrink-0 text-default-400"
			aria-hidden="true"
		>
			<circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
			<path
				d="M21 21l-4.35-4.35"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
			/>
		</svg>
	);
}

export default FilterMultiSelect;
